import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Modal } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useAuth } from '@/store/AuthContext';
import { getApiErrorMessage } from '@/utils/dashboard/apiError';
import type { AdminKiosk, KioskPayload } from '@/types/dashboard/kiosk';
import { useVendors } from '../VendorsPage/useVendors';
import { useRateCards } from '../RatesPage/useRates';
import { useCreateKiosk, useUpdateKiosk } from './useKiosks';

interface FormValues {
  vendor: string;
  rate_charge: string; // id of a rate card onboarded earlier on the Rates page
  name: string;
  location: string;
  printer_model: string;
  printer_specification: string;
  kiosk_serial_number: string;
  is_a4: boolean;
  is_letter: boolean;
  is_legal: boolean;
  a4_count: string;
  letter_count: string;
  legal_count: string;
  is_active: boolean;
}
type FormErrors = Partial<Record<keyof FormValues, string>>;

interface Props {
  kiosk: AdminKiosk | null; // null = add new kiosk
  onClose: () => void;
}

const isCount = (s: string) => /^\d+$/.test(s.trim());
const percent = (fraction: string) => Math.round(Number(fraction) * 10000) / 100;
// What the customer pays per page: the rate plus the merchant commission
const finalRate = (rate: string, commission: string) => (Number(rate) + Number(commission)).toFixed(2);

function validate(v: FormValues): FormErrors {
  const e: FormErrors = {};
  if (!v.vendor) e.vendor = 'Select a vendor';
  if (!v.rate_charge) e.rate_charge = 'Select a rate card';
  if (!v.name.trim()) e.name = 'Kiosk name is required';
  if (!v.location.trim()) e.location = 'Location is required';
  if (!v.printer_model.trim()) e.printer_model = 'Printer model is required';
  if (!v.kiosk_serial_number.trim()) e.kiosk_serial_number = 'Serial number is required';
  if (v.is_a4 && !isCount(v.a4_count)) e.a4_count = 'Enter a whole number';
  if (v.is_letter && !isCount(v.letter_count)) e.letter_count = 'Enter a whole number';
  if (v.is_legal && !isCount(v.legal_count)) e.legal_count = 'Enter a whole number';
  if (!v.is_a4 && !v.is_letter && !v.is_legal) e.is_a4 = 'Select at least one paper size';
  return e;
}

function Field({ label, hint, error, children }: { label: string; hint?: string; error?: string; children: ReactNode }) {
  return (
    <div className="mb-3">
      <label className="text-label-md text-on-surface mb-1 d-block">{label}</label>
      {children}
      {hint && !error && <div className="text-body-sm text-on-surface-variant mt-1">{hint}</div>}
      {error && <div className="text-danger text-body-sm mt-1">{error}</div>}
    </div>
  );
}

export function KioskFormModal({ kiosk, onClose }: Props) {
  const { user } = useAuth();
  const isEdit = kiosk !== null;
  const lockedVendorId = user?.scope.vendor_id ?? null;
  const { data: vendors = [] } = useVendors();
  const { data: rateCards = [] } = useRateCards();
  const createKiosk = useCreateKiosk();
  const updateKiosk = useUpdateKiosk();

  const [values, setValues] = useState<FormValues>({
    vendor: String(kiosk?.vendor ?? lockedVendorId ?? ''),
    rate_charge: String(kiosk?.rate_charge?.id ?? ''),
    name: kiosk?.name ?? '',
    location: kiosk?.location ?? '',
    printer_model: kiosk?.printer_model ?? '',
    printer_specification: kiosk?.printer_specification ?? '',
    kiosk_serial_number: kiosk?.kiosk_serial_number ?? '',
    is_a4: kiosk?.is_a4 ?? true,
    is_letter: kiosk?.is_letter ?? false,
    is_legal: kiosk?.is_legal ?? false,
    a4_count: String(kiosk?.a4_count ?? 0),
    letter_count: String(kiosk?.letter_count ?? 0),
    legal_count: String(kiosk?.legal_count ?? 0),
    is_active: kiosk?.is_active ?? true,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const set = <K extends keyof FormValues>(key: K, val: FormValues[K]) =>
    setValues((p) => ({ ...p, [key]: val }));
  const invalid = (k: keyof FormValues) => (errors[k] ? ' is-invalid' : '');

  const text = (key: keyof FormValues, label: string, extra?: { inputMode?: 'decimal' | 'numeric' }) => (
    <Field label={label} error={errors[key]}>
      <input
        className={`form-control${invalid(key)}`}
        value={values[key] as string}
        onChange={(e) => set(key, e.target.value as never)}
        inputMode={extra?.inputMode}
      />
    </Field>
  );

  const check = (key: keyof FormValues, label: string) => (
    <div className="form-check form-switch">
      <input
        id={`kiosk-${key}`}
        type="checkbox"
        className="form-check-input"
        checked={values[key] as boolean}
        onChange={(e) => set(key, e.target.checked as never)}
      />
      <label className="form-check-label text-label-md" htmlFor={`kiosk-${key}`}>
        {label}
      </label>
    </div>
  );

  // Active rate cards of the chosen vendor. A kiosk's current card stays listed even if it was deactivated.
  const vendorRates = useMemo(
    () =>
      rateCards.filter(
        (r) => String(r.vendor) === values.vendor && (r.is_active || String(r.id) === values.rate_charge),
      ),
    [rateCards, values.vendor, values.rate_charge],
  );

  const selectedRate = vendorRates.find((r) => String(r.id) === values.rate_charge) ?? null;

  const onVendorChange = (vendor: string) =>
    // a rate card belongs to one vendor, so changing vendor clears the choice
    setValues((p) => ({ ...p, vendor, rate_charge: '' }));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const errs = validate(values);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitError('');
    setSubmitting(true);
    try {
      const payload: KioskPayload = {
        vendor: Number(values.vendor),
        rate_charge: Number(values.rate_charge),
        name: values.name.trim(),
        location: values.location.trim(),
        printer_model: values.printer_model.trim(),
        printer_specification: values.printer_specification.trim(),
        kiosk_serial_number: values.kiosk_serial_number.trim(),
        is_a4: values.is_a4,
        is_letter: values.is_letter,
        is_legal: values.is_legal,
        a4_count: values.is_a4 ? Number(values.a4_count) : 0,
        letter_count: values.is_letter ? Number(values.letter_count) : 0,
        legal_count: values.is_legal ? Number(values.legal_count) : 0,
        is_active: values.is_active,
      };

      if (kiosk) {
        await updateKiosk.mutateAsync({ id: kiosk.id, payload });
      } else {
        await createKiosk.mutateAsync(payload);
      }
      toast.success(kiosk ? 'Kiosk updated' : 'Kiosk created');
      onClose();
    } catch (err) {
      setSubmitError(getApiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show onHide={submitting ? undefined : onClose} centered size="lg" backdrop="static" scrollable>
      {/* flex column + min-height 0 keeps Bootstrap's scrollable body working through the form wrapper */}
      <form onSubmit={onSubmit} noValidate className="d-flex flex-column overflow-hidden" style={{ minHeight: 0 }}>
        <Modal.Header closeButton={!submitting}>
          <Modal.Title className="text-headline-sm">{isEdit ? 'Edit kiosk' : 'Add kiosk'}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="row">
            <div className="col-md-6">
              <Field label="Vendor" error={errors.vendor}>
                <select
                  className={`form-select${invalid('vendor')}`}
                  value={values.vendor}
                  onChange={(e) => onVendorChange(e.target.value)}
                  disabled={lockedVendorId !== null}
                >
                  <option value="">Select vendor…</option>
                  {lockedVendorId !== null && !vendors.some((v) => v.id === lockedVendorId) && (
                    <option value={lockedVendorId}>{kiosk?.vendor_name ?? 'Your vendor'}</option>
                  )}
                  {vendors.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="col-md-6">
              <Field
                label="Rate card"
                hint={
                  values.vendor && vendorRates.length === 0
                    ? 'This vendor has no rate card yet. Onboard one on the Rates page first.'
                    : undefined
                }
                error={errors.rate_charge}
              >
                <select
                  className={`form-select${invalid('rate_charge')}`}
                  value={values.rate_charge}
                  onChange={(e) => set('rate_charge', e.target.value)}
                  disabled={!values.vendor || vendorRates.length === 0}
                >
                  <option value="">{values.vendor ? 'Select rate card…' : 'Select a vendor first'}</option>
                  {vendorRates.map((r) => (
                    <option key={r.id} value={r.id}>
                      {`#${r.id} · B/W ₹${finalRate(r.bw_rate_per_page, r.merchant_fees)} · Colour ₹${finalRate(r.color_rate_per_page, r.merchant_fees)}${r.is_active ? '' : ' (inactive)'}`}
                    </option>
                  ))}
                </select>
              </Field>
              {selectedRate && (
                <div className="bg-surface-low rounded-3 p-3 mb-3 text-body-sm text-on-surface-variant">
                  <div className="text-label-md text-on-surface mb-1">Customer price per page</div>
                  <div>
                    B/W ₹{Number(selectedRate.bw_rate_per_page).toFixed(2)} + ₹{Number(selectedRate.merchant_fees).toFixed(2)}{' '}
                    commission = <strong>₹{finalRate(selectedRate.bw_rate_per_page, selectedRate.merchant_fees)}</strong>
                  </div>
                  <div>
                    Colour ₹{Number(selectedRate.color_rate_per_page).toFixed(2)} + ₹
                    {Number(selectedRate.merchant_fees).toFixed(2)} commission ={' '}
                    <strong>₹{finalRate(selectedRate.color_rate_per_page, selectedRate.merchant_fees)}</strong>
                  </div>
                  <div>
                    Duplex {percent(selectedRate.duplex_extra_charge)}% off · Convenience fee ₹
                    {Number(selectedRate.convenience_fee).toFixed(2)} per print
                  </div>
                </div>
              )}
            </div>
            <div className="col-md-6">{text('name', 'Kiosk name')}</div>
            <div className="col-md-6">{text('location', 'Location')}</div>
            <div className="col-md-6">{text('kiosk_serial_number', 'Kiosk serial number')}</div>
            <div className="col-md-6">{text('printer_model', 'Printer model')}</div>
            <div className="col-md-6">{text('printer_specification', 'Printer specification')}</div>
          </div>

          <div className="bg-surface-low rounded-3 p-3 mb-3">
            <div className="text-label-md text-on-surface mb-3">Paper trays</div>
            {errors.is_a4 && <div className="text-danger text-body-sm mb-2">{errors.is_a4}</div>}
            <div className="row align-items-end">
              {(
                [
                  ['is_a4', 'A4', 'a4_count'],
                  ['is_letter', 'Letter', 'letter_count'],
                  ['is_legal', 'Legal', 'legal_count'],
                ] as const
              ).map(([flag, label, count]) => (
                <div className="col-md-4" key={flag}>
                  <div className="mb-2">{check(flag, label)}</div>
                  {values[flag] && text(count, `${label} sheets loaded`, { inputMode: 'numeric' })}
                </div>
              ))}
            </div>
          </div>

          {check('is_active', 'Active')}

          {submitError && (
            <div className="alert alert-danger d-flex gap-2 mt-3 mb-0 py-2" role="alert">
              <i className="bi bi-exclamation-circle" />
              <span className="text-body-sm">{submitError}</span>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Saving…
              </>
            ) : isEdit ? (
              'Save changes'
            ) : (
              'Add kiosk'
            )}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
